import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Mail, RefreshCw, Clock, User, FileText, CheckCircle, AlertCircle, Loader2, Power, AlertTriangle, HelpCircle, RotateCcw, Zap } from 'lucide-react';
import ServiceLLMToggle from '../components/ServiceLLMToggle';
import GlobalModeToggle from '../components/GlobalModeToggle';

const Tribunes = () => {
  const [tribunes, setTribunes] = useState([]);
  const [emailConfig, setEmailConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [checkingIfrap, setCheckingIfrap] = useState(false);
  const [serviceEnabled, setServiceEnabled] = useState(true);
  const [togglingService, setTogglingService] = useState(false);
  
  // IFRAP-API tunnel state
  const [ifrapEnabled, setIfrapEnabled] = useState(false);
  const [togglingIfrap, setTogglingIfrap] = useState(false);
  const [userGrade, setUserGrade] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [tribunesRes, configRes, statusRes] = await Promise.all([
        axios.get(`${API}/tribunes/email`),
        axios.get(`${API}/tribunes/email-config`),
        axios.get(`${API}/services/status`)
      ]);
      setTribunes(tribunesRes.data);
      setEmailConfig(configRes.data);
      setServiceEnabled(statusRes.data.tribunes);
      
      // Load IFRAP-API status (for amiral users)
      try {
        const ifrapRes = await axios.get(`${API}/ifrap-api/status`);
        // Utiliser 'enabled' (état DB persisté) comme source de vérité pour l'UI
        // Si 'enabled' n'existe pas (ancien backend), fallback sur 'running'
        if (ifrapRes.data) {
          const isEnabled = ifrapRes.data.enabled !== undefined 
            ? ifrapRes.data.enabled 
            : ifrapRes.data.running;
          setIfrapEnabled(isEnabled);
        }
      } catch (ifrapErr) {
        // Don't reset ifrapEnabled on error - keep previous state
        // This prevents the toggle from flickering on network issues
        console.debug('[Tribunes] IFRAP status not accessible (expected for non-amiral):', ifrapErr.message);
      }
      
      // Check user grade
      try {
        const meRes = await axios.get(`${API}/auth/me`);
        setUserGrade(meRes.data.grade);
      } catch (authErr) {
        console.debug('[Tribunes] Auth check failed:', authErr.message);
      }
    } catch (error) {
      console.error('Error loading tribunes:', error);
      toast.error('Erreur lors du chargement des tribunes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Auto-refresh toutes les 30 secondes
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const toggleService = async () => {
    setTogglingService(true);
    try {
      const response = await axios.post(`${API}/services/tribunes/toggle`);
      setServiceEnabled(response.data.enabled);
      toast.success(response.data.enabled ? 'Service Tribunes activé' : 'Service Tribunes suspendu');
    } catch (error) {
      toast.error('Erreur lors du changement d\'état');
    } finally {
      setTogglingService(false);
    }
  };

  const toggleIfrapService = async () => {
    setTogglingIfrap(true);
    try {
      if (ifrapEnabled) {
        await axios.post(`${API}/ifrap-api/stop`);
        setIfrapEnabled(false);
        toast.success('Tunnel IFRAP-AI arrêté');
      } else {
        await axios.post(`${API}/ifrap-api/start`);
        setIfrapEnabled(true);
        toast.success('Tunnel IFRAP-AI démarré');
      }
    } catch (error) {
      toast.error('Erreur lors du changement d\'état IFRAP');
      // Reload actual state from server to ensure UI is in sync
      try {
        const ifrapRes = await axios.get(`${API}/ifrap-api/status`);
        if (ifrapRes.data) {
          const isEnabled = ifrapRes.data.enabled !== undefined 
            ? ifrapRes.data.enabled 
            : ifrapRes.data.running;
          setIfrapEnabled(isEnabled);
        }
      } catch (e) {
        // Ignore - keep current state
      }
    } finally {
      setTogglingIfrap(false);
    }
  };

  const forceCheck = async () => {
    setChecking(true);
    try {
      await axios.post(`${API}/tribunes/email/check-now`);
      toast.success('Vérification des boîtes mail lancée');
      // Attendre un peu puis recharger
      setTimeout(loadData, 3000);
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    } finally {
      setChecking(false);
    }
  };

  const forceCheckIfrap = async () => {
    setCheckingIfrap(true);
    try {
      const response = await axios.post(`${API}/ifrap-api/check-now`);
      const data = response.data;
      if (data.new_articles > 0) {
        toast.success(`IFRAP-AI: ${data.new_articles} nouvel(s) article(s) récupéré(s)`);
      } else {
        toast.info('IFRAP-AI: Aucun nouvel article à traiter');
      }
      // Recharger après 3 secondes pour voir les nouveaux articles
      setTimeout(loadData, 3000);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la vérification IFRAP';
      toast.error(msg);
    } finally {
      setCheckingIfrap(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', variant: 'secondary', icon: Clock },
      processing: { label: 'Traitement...', variant: 'default', icon: Loader2 },
      draft_created: { label: 'Brouillon créé', variant: 'success', icon: CheckCircle },
      published: { label: 'Publié', variant: 'success', icon: CheckCircle },
      wp_failed: { label: 'Échec WP', variant: 'destructive', icon: AlertCircle },
      error: { label: 'Erreur', variant: 'destructive', icon: AlertCircle },
      needs_review: { label: 'À arbitrer', variant: 'warning', icon: AlertTriangle },
      no_tribune: { label: 'Pas une tribune', variant: 'outline', icon: HelpCircle },
    };

    const config = statusConfig[status] || { label: status || 'inconnu', variant: 'outline', icon: HelpCircle };
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Une tribune est "bloquée" si elle est en pending/processing depuis plus de 30 min
  const isStuck = (tribune) => {
    if (!['pending', 'processing'].includes(tribune.status)) return false;
    if (!tribune.created_at) return false;
    const created = new Date(tribune.created_at);
    const ageMin = (Date.now() - created.getTime()) / 60000;
    return ageMin > 30;
  };

  const canRetry = (tribune) => tribune.status === 'error' || tribune.status === 'wp_failed' || isStuck(tribune);

  const retryTribune = async (tribuneId) => {
    try {
      const r = await axios.post(`${API}/tribunes/email/${tribuneId}/retry`);
      toast.success(`Retraitement lancé (${r.data.previous_status} → pending)`);
      if (r.data.warning) toast.info(r.data.warning, { duration: 8000 });
      setTimeout(loadData, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur retry');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" data-testid="tribunes-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 
            className="text-2xl md:text-3xl font-bold tracking-wider uppercase mb-2"
            style={{ 
              fontFamily: "'Orbitron', monospace",
              color: '#38bdf8',
              textShadow: '0 0 20px rgba(56,189,248,0.3)'
            }}
          >
            TRIBUNES_EMAIL
          </h1>
          <p className="text-slate-500 font-mono text-sm">
            Réception automatique des tribunes par email
          </p>
        </div>
        
        <div className="flex items-center gap-4 flex-wrap">
          {/* Toggle LLM Mode */}
          <ServiceLLMToggle serviceId="tribunes" />
          
          {/* Toggle Service ON/OFF */}
          <div className="flex items-center gap-3 bg-slate-800/50 px-4 py-2 rounded-lg border border-slate-700">
            <Power className={`w-4 h-4 ${serviceEnabled ? 'text-emerald-400' : 'text-slate-500'}`} />
            <span className="text-sm text-slate-300 font-mono">Service</span>
            <Switch
              checked={serviceEnabled}
              onCheckedChange={toggleService}
              disabled={togglingService}
              data-testid="toggle-tribunes-service"
            />
            <span className={`text-xs font-mono ${serviceEnabled ? 'text-emerald-400' : 'text-red-400'}`}>
              {serviceEnabled ? 'ON' : 'OFF'}
            </span>
          </div>

          {/* Toggle IFRAP-AI Tunnel (Amiraux uniquement) */}
          {userGrade === 'amiral' && (
            <div className="flex items-center gap-3 bg-purple-950/50 px-4 py-2 rounded-lg border border-purple-500/50">
              <Zap className={`w-4 h-4 ${ifrapEnabled ? 'text-purple-400' : 'text-slate-500'}`} />
              <span className="text-sm text-purple-300 font-mono">IFRAP-AI</span>
              <Switch
                checked={ifrapEnabled}
                onCheckedChange={toggleIfrapService}
                disabled={togglingIfrap}
                data-testid="toggle-ifrap-service"
              />
              <span className={`text-xs font-mono ${ifrapEnabled ? 'text-purple-400' : 'text-red-400'}`}>
                {ifrapEnabled ? 'ON' : 'OFF'}
              </span>
              {/* Bouton Vérifier maintenant IFRAP */}
              <Button
                onClick={forceCheckIfrap}
                disabled={checkingIfrap || !ifrapEnabled}
                size="sm"
                className="ml-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-xs"
                data-testid="check-ifrap-btn"
              >
                {checkingIfrap ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3 mr-1" />
                )}
                Vérifier
              </Button>
            </div>
          )}

          {/* Switch Global Mode (Emergent/Direct) - Amiraux uniquement */}
          <GlobalModeToggle />

          <Button
            onClick={forceCheck}
            disabled={checking || !serviceEnabled}
            className="bg-sky-600 hover:bg-sky-500 text-white font-semibold disabled:opacity-50"
            data-testid="check-emails-btn"
          >
            {checking ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Vérifier maintenant
          </Button>
        </div>
      </div>

      {/* Service Suspended Banner */}
      {!serviceEnabled && (
        <Card className="mb-6 bg-amber-900/20 border-amber-500/30">
          <CardContent className="py-4">
            <div className="flex items-center gap-3 text-amber-400">
              <AlertCircle className="w-5 h-5" />
              <span className="font-mono">Service suspendu — Les emails ne sont pas traités automatiquement</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Config Status */}
      {emailConfig && (
        <Card className="mb-6 bg-slate-900/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-mono text-slate-400 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              BOÎTES MAIL SURVEILLÉES
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {emailConfig.inboxes.map((inbox) => (
                <div 
                  key={inbox.email}
                  className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded border border-slate-700"
                >
                  <div className={`w-2 h-2 rounded-full ${inbox.enabled ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                  <span className="font-mono text-sm text-sky-300">{inbox.email}</span>
                  <span className="text-slate-500 text-xs">→ {inbox.target_site_domain}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3 font-mono">
              Vérification automatique toutes les {emailConfig.check_interval_minutes} minutes
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tribunes List */}
      {tribunes.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-700">
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 mx-auto mb-4 text-slate-600" />
            <p className="text-slate-400 font-mono">Aucune tribune reçue pour le moment</p>
            <p className="text-slate-500 text-sm mt-2">
              Les emails envoyés à <span className="text-sky-400">tribunes@economiematin.fr</span> apparaîtront ici
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {tribunes.map((tribune) => (
            <Card 
              key={tribune.id} 
              className="bg-slate-900/50 border-slate-700 hover:border-sky-500/30 transition-colors"
              data-testid={`tribune-${tribune.id}`}
            >
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(tribune.status)}
                      <span className="text-xs text-slate-500 font-mono">
                        {formatDate(tribune.created_at)}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {tribune.subject}
                    </h3>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{tribune.author_name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        <span>{tribune.source_email}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span>{tribune.target_site_name}</span>
                      </div>
                    </div>
                    
                    {tribune.error_message && (
                      <div className="mt-3 p-2 bg-red-900/20 border border-red-500/30 rounded text-red-400 text-sm">
                        {tribune.error_message}
                      </div>
                    )}

                    {/* Raison LLM (needs_review / no_tribune) */}
                    {tribune.llm_reason && ['needs_review', 'no_tribune'].includes(tribune.status) && (
                      <div className="mt-3 p-2 bg-amber-900/20 border border-amber-500/30 rounded text-amber-300 text-sm" data-testid={`tribune-llm-reason-${tribune.id}`}>
                        <span className="font-semibold">LLM&nbsp;:</span> {tribune.llm_reason}
                      </div>
                    )}

                    {/* Échec publication WP : article créé en DB mais pas posté sur WP */}
                    {tribune.status === 'draft_created' && tribune.article_id && !tribune.wordpress_post_id && (
                      <div className="mt-3 p-2 bg-orange-900/20 border border-orange-500/30 rounded text-orange-300 text-sm">
                        ⚠️ Article généré mais publication WordPress échouée. Republier manuellement.
                      </div>
                    )}

                    {/* Tribune coincée depuis > 30 min */}
                    {isStuck(tribune) && (
                      <div className="mt-3 p-2 bg-yellow-900/20 border border-yellow-500/30 rounded text-yellow-300 text-sm">
                        ⏱ Bloquée depuis plus de 30 min. Cliquez sur « Relancer » à droite.
                      </div>
                    )}

                    {tribune.retry_count > 0 && (
                      <p className="mt-2 text-xs text-slate-500 font-mono">
                        Retry: {tribune.retry_count}× — dernier {formatDate(tribune.retry_triggered_at)}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex flex-col gap-2 items-end">
                  {tribune.wordpress_post_id && tribune.target_site_url ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-sky-500/50 text-sky-400 hover:bg-sky-500/10"
                      data-testid={`tribune-view-${tribune.id}`}
                      onClick={() => window.open(`${tribune.target_site_url}/wp-admin/post.php?post=${tribune.wordpress_post_id}&action=edit`, '_blank')}
                    >
                      Voir l&apos;article
                    </Button>
                  ) : tribune.article_id && (
                    <span className="text-xs text-slate-500 font-mono">En attente de publication</span>
                  )}

                  {canRetry(tribune) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                      data-testid={`tribune-retry-${tribune.id}`}
                      onClick={() => retryTribune(tribune.id)}
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Relancer
                    </Button>
                  )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Tribunes;
